import React, { useId, useState } from 'react';
import {
  Box,
  IconButton,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import { getGlossaryIllustration } from '../../constants/glossaryIllustrations';
import { getGlossaryTerm } from '../../constants/glossaryTerms';
import useReadinessLocale from '../../hooks/useReadinessLocale';
import { WORKFLOW_GUIDE_LOCALE } from '../../i18n/glossaryLocale';

export interface PlainLanguageExplainerProps {
  termId: string;
  /** Visible label; defaults to glossary term. */
  children?: React.ReactNode;
  variant?: 'inline' | 'heading' | 'icon';
}

const PlainLanguageExplainer: React.FC<PlainLanguageExplainerProps> = ({
  termId,
  children,
  variant = 'inline',
}) => {
  const { locale } = useReadinessLocale();
  const ui = WORKFLOW_GUIDE_LOCALE[locale];
  const entry = getGlossaryTerm(termId, locale);
  const illustration = getGlossaryIllustration(termId, locale);
  const label = children ?? entry?.term ?? termId;
  const titleId = useId();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!entry) {
    return <>{label}</>;
  }

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const popoverBody = (
    <Box sx={{ maxWidth: 340, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
        <Typography id={titleId} variant="subtitle1" sx={{ fontWeight: 700, pr: 1 }}>
          {entry.term}
        </Typography>
        <IconButton size="small" aria-label={ui.closeExplanation} onClick={handleClose} sx={{ mt: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {illustration ? (
        <Box
          component="img"
          src={illustration.src}
          alt={illustration.alt}
          sx={{
            width: '100%',
            maxHeight: 140,
            objectFit: 'contain',
            borderRadius: 1,
            bgcolor: 'action.hover',
            mb: 1.5,
            p: 1,
          }}
        />
      ) : null}

      <Typography variant="body2" color="text.secondary" sx={{ mb: entry.example ? 1 : 0 }}>
        {entry.plain}
      </Typography>

      {entry.example ? (
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
          {ui.examplePrefix} {entry.example}
        </Typography>
      ) : null}
    </Box>
  );

  if (variant === 'icon') {
    return (
      <>
        <IconButton
          size="small"
          aria-label={`Explain ${entry.term}`}
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={handleOpen}
          sx={{ p: 0.25, ml: 0.25 }}
        >
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { 'aria-labelledby': titleId } }}
        >
          {popoverBody}
        </Popover>
      </>
    );
  }

  const triggerSx =
    variant === 'heading'
      ? { display: 'inline-flex', alignItems: 'center', gap: 0.5 }
      : {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          borderBottom: '1px dotted',
          borderColor: 'text.disabled',
          cursor: 'pointer',
        };

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={handleOpen}
        aria-label={`${label}. ${ui.tapHint}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        sx={{
          ...triggerSx,
          background: 'none',
          border: variant === 'heading' ? 'none' : undefined,
          borderBottom: variant === 'heading' ? undefined : '1px dotted',
          borderColor: variant === 'heading' ? undefined : 'text.disabled',
          padding: 0,
          font: 'inherit',
          color: 'inherit',
          cursor: 'pointer',
          textAlign: 'inherit',
        }}
      >
        {variant === 'heading' ? (
          <Typography component="span" variant="inherit" sx={{ fontWeight: 'inherit' }}>
            {label}
          </Typography>
        ) : (
          label
        )}
        <HelpOutlineIcon
          sx={{ fontSize: variant === 'heading' ? 16 : 14, color: 'text.secondary' }}
          aria-hidden
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: isMobile ? 'center' : 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: isMobile ? 'center' : 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            'aria-labelledby': titleId,
            sx: isMobile ? { mx: 1, maxWidth: 'calc(100vw - 16px)' } : undefined,
          },
        }}
      >
        {popoverBody}
      </Popover>
    </>
  );
};

export default PlainLanguageExplainer;
